import React, { useCallback, useEffect, useRef, useState } from 'react';
import { crmFormsApi } from '../../services/api';
import { formsOrigin } from '../../utils/forms';

// Live preview: iframe on the backend-served /f/:slug?preview=1&pt=... page. The schema being
// edited is pushed via postMessage (the renderer only accepts allowlisted origins and applies
// the schema through DOM text APIs, so nothing from the schema is ever injected as HTML here).
export default function FormPreview({ formId, slug, schema }) {
  const iframeRef = useRef(null);
  const schemaRef = useRef(schema);
  const [path, setPath] = useState(null);
  const [error, setError] = useState(null);
  const origin = formsOrigin();

  schemaRef.current = schema;

  const loadToken = useCallback(async () => {
    setError(null);
    setPath(null);
    try {
      const res = await crmFormsApi.createPreviewToken(formId);
      setPath(res.data.path);
    } catch (err) {
      setError(err.message || 'Não foi possível gerar a prévia');
    }
  }, [formId]);

  // New token whenever the form (or its saved slug) changes.
  useEffect(() => { loadToken(); }, [loadToken, slug]);

  const post = useCallback(() => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    try {
      win.postMessage({ type: 'crm-form-preview', schema: JSON.parse(JSON.stringify(schemaRef.current)) }, origin);
    } catch { /* ignore */ }
  }, [origin]);

  // Debounced push on every edit.
  useEffect(() => {
    if (!path) return undefined;
    const t = setTimeout(post, 250);
    return () => clearTimeout(t);
  }, [schema, path, post]);

  // The renderer announces readiness; answer with the current schema.
  useEffect(() => {
    const onMsg = (e) => {
      if (e.origin !== origin || e.source !== iframeRef.current?.contentWindow) return;
      if (e.data?.type === 'crm-form-ready') post();
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [origin, post]);

  return (
    <div className="card p-3 flex flex-col h-full">
      <div className="flex items-center justify-between px-1 pb-2">
        <span className="text-xs font-bold uppercase tracking-wider text-secondary">Prévia</span>
        <button type="button" onClick={loadToken} className="text-xs font-bold text-primary flex items-center gap-1">
          <span className="material-symbols-outlined text-[16px]">refresh</span>Atualizar preview
        </button>
      </div>
      {error && <p className="text-sm text-red-600 p-2">{error}</p>}
      {!error && !path && <p className="text-sm text-slate-500 p-2">Carregando prévia...</p>}
      {path && (
        <iframe
          ref={iframeRef}
          title="Prévia do formulário"
          src={`${origin}${path}`}
          onLoad={post}
          className="w-full flex-1 min-h-[520px] rounded-lg border border-slate-200 bg-white"
          sandbox="allow-scripts allow-same-origin allow-forms"
        />
      )}
    </div>
  );
}
